/* IBM Confidential
*
* OCO Source Materials
*

* IBM Cognos Products: ba-ui-toolkit
* (C) Copyright IBM Corp. 2019
*
* The source code for this program is not published or otherwise
* divested of its trade secrets, irrespective of what has been
* deposited with the U.S. Copyright Office.
* NLS_ENCODING=UTF-8
* NLS_MESSAGEFORMAT_NONE
*/
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
	classnames as cx,
	keyboardUtils,
	bidiUtils,
	propsUtils,
	lodash,
} from '@ba-ui-toolkit/utils';
import { createIcon as createSVGIcon } from '@ba-ui-toolkit/svgicon';
import Link from '@ba-ui-toolkit/link';
import styles from './BaseInput.scss';

const { uniqueId } = lodash;
const { sanitize } = propsUtils;
const { isKey, keyNames } = keyboardUtils;
const { resolveBaseTextDir } = bidiUtils;

const _CLASS = styles.baseInputClassName.replace(/"/g, '');

function createIcon(icon, locationClass) {
	return createSVGIcon(icon, null, _CLASS, locationClass);
}

export default class BaseInput extends Component {
	static propTypes = {
		id: PropTypes.string,
		'data-tid': PropTypes.string,
		className: PropTypes.string,
		title: PropTypes.string,
		type: PropTypes.oneOf(['password', 'time']),
		textAlign: PropTypes.oneOf(['left', 'right', 'center']),
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		dir: PropTypes.oneOf(['ltr', 'rtl']),
		fullWidth: PropTypes.bool,
		icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
		iconLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
		iconRight: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
		disabled: PropTypes.bool,
		readOnly: PropTypes.bool,
		active: PropTypes.bool,
		hover: PropTypes.bool,
		onChange: PropTypes.func,
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		children: PropTypes.node,
		fireOnBlur: PropTypes.bool,
		onKeyDown: PropTypes.func,
		onBlur: PropTypes.func,
		autoFocus: PropTypes.bool,
		autoSelect: PropTypes.bool,
		validationErrorText: PropTypes.string,
		successText: PropTypes.string,
		hasValidationError: PropTypes.bool,
		isMoreInfo: PropTypes.bool,
		moreInfoClickHandler: PropTypes.func,
		onFocus: PropTypes.func,
		onClick: PropTypes.func,
		moreInfoText: PropTypes.string,
		noBottomBorder: PropTypes.bool,
		style: PropTypes.object,
		placeholder: PropTypes.string,
		ariaLabelledBy: PropTypes.string,
		onInput: PropTypes.func,
		onValueChange: PropTypes.func,
		onValueAccept: PropTypes.func,
	};

	static defaultProps = {
		noBottomBorder: false,
		className: '',
		style: {},
		ariaLabelledBy: '',
	};

	_blurFlushed = true;

	// eventually/hopefully onValueChange/onValueAccept will be the norm, and useOnValueCallbacks can be removed
	_useOnValueCallbacks = !!this.props.onValueChange || !!this.props.onValueAccept;

	constructor(props) {
		super(props);
		this.state = {
			displayValue: undefined,
			showFocusClass: false,
		};
	}

	componentDidMount() {
		const { autoFocus, autoSelect } = this.props;

		this.setState((state, prop) => ({ displayValue: prop.value }));

		autoFocus && this.focus();
		autoSelect && this.select();

		// eslint-disable-next-line react/prop-types
		const { fireOnBlur } = this.props;
		if (fireOnBlur) {
			console.warn(
				'Deprecated - use onValueChange/onValueAccept to replace fireOnBlur/onChange/onBlur'
			);
		}

		// eslint-disable-next-line react/prop-types
		const { onChange } = this.props;
		if (onChange) {
			console.warn(
				'Warning - consider using onValueChange/onValueAccept: the behaviour of onChange will change to the native event when onValueChange/onValueAccept becomes the default behaviour'
			);
		}

		// eslint-disable-next-line react/prop-types
		const {
			allowInvalidEntry,
			validEntryMatch,
			onValueChangeInvalid,
			onValueBlur,
			onValueBlurInvalid,
		} = this.props;
		if (
			allowInvalidEntry ||
			validEntryMatch ||
			onValueChangeInvalid ||
			onValueBlur ||
			onValueBlurInvalid
		) {
			console.log(
				'Deprecated - use onValueChange/onValueAccept to replace allowInvalidEntry/validEntryMatch/onValueChangeInvalid/onValueBlur/onValueBlurInvalid'
			);
		}

		// eslint-disable-next-line react/prop-types
		const { showIsReqWhenValidErrTxt, labelRequired } = this.props;
		if (showIsReqWhenValidErrTxt || labelRequired) {
			console.warn(
				'Deprecated - showIsReqWhenValidErrTxt/labelRequired; "Required field"/"labelRequired" no longer supported; 289747'
			);
		}
	}

	componentWillReceiveProps({ value: newValue }) {
		const { fireOnBlur, value: oldValue } = this.props;

		if (this._useOnValueCallbacks && newValue !== oldValue) {
			this.setState({ displayValue: newValue });
		} else if (fireOnBlur && newValue !== oldValue) {
			this._blurFlushed = true;
			this.setState({ displayValue: newValue });
		}
	}

	componentWillUnmount() {
		const { value: oldValue, fireOnBlur, onChange } = this.props;
		const { displayValue: newValue } = this.state;

		if (this._useOnValueCallbacks) {
			this._onValueAccept(newValue);
		} else if (fireOnBlur && !this._blurFlushed && newValue !== oldValue) {
			// If fireOnBlur and there is an onChange queued then call it before unmount
			// This is to ensure that the value doesn't get lost
			onChange && onChange(newValue);
		}
	}

	focus() {
		const { disabled } = this.props;

		!disabled && this.inputNode && this.inputNode.focus();
	}

	select() {
		const { disabled } = this.props;

		!disabled && this.inputNode && this.inputNode.select();
	}

	_onChange = e => {
		const { onChange, fireOnBlur } = this.props;
		const { value: newValue } = e.target;

		if (this._useOnValueCallbacks) {
			this._onValueChange(newValue);
			onChange && onChange(e);
		} else if (fireOnBlur) {
			this.setState({ displayValue: newValue });
			this._blurFlushed = false;
		} else {
			onChange && onChange(newValue, e);
		}
	};

	_onValueChange = newValue => {
		const { onValueChange } = this.props;

		onValueChange && onValueChange(newValue);
	};

	_notify = e => {
		const { value: oldValue, onChange } = this.props;
		const { displayValue: newValue } = this.state;

		this._blurFlushed = true;
		if (newValue !== oldValue) {
			onChange && onChange(newValue, e);
		}
	};

	_onClick = () => {
		const { onClick } = this.props;

		onClick && onClick();
		this.setState({ showFocusClass: false });
	};

	_onKeyDown = e => {
		const { onKeyDown, fireOnBlur, disabled } = this.props;
		const { value: newValue } = e.target;

		if (isKey(e, keyNames.ENTER)) {
			if (this._useOnValueCallbacks) {
				this._onValueAccept(newValue);
			} else if (fireOnBlur) {
				this._notify(e);
			}
		}

		// 9 = Tab key
		if (isKey(e, keyNames.TAB)) {
			!disabled && this.setState({ showFocusClass: true });
		} else {
			this.setState({ showFocusClass: false });
		}

		onKeyDown && onKeyDown(e);
	};

	_onKeyUp = e => {
		const { disabled } = this.props;
		const { TAB } = keyNames;

		if (isKey(e, TAB)) {
			!disabled && this.setState({ showFocusClass: true });
		}
	};

	_onBlur = e => {
		const { onBlur, fireOnBlur } = this.props;
		const { value: newValue } = e.target;

		if (this._useOnValueCallbacks) {
			this._onValueAccept(newValue);
		} else {
			fireOnBlur && this._notify(e);
		}

		onBlur && onBlur(e);
		this.setState({ showFocusClass: false });
	};

	_onValueAccept = newValue => {
		const { onValueAccept } = this.props;

		// toolkit widgets that call BaseInput need to check if
		// newValue !== oldValue before calling their onValueAccept
		// DO NOT do the check here, each widget applies prop limit checks
		onValueAccept && onValueAccept(newValue);
	};

	_onFocus = e => {
		const { onFocus } = this.props;

		onFocus && onFocus(e);
	};

	_onMoreInfoClick = e => {
		const { isMoreInfo, moreInfoClickHandler } = this.props;

		isMoreInfo && moreInfoClickHandler(e);
	};

	_id = uniqueId('ba-common-base-input');

	render() {
		// extract props that are to be manipulated or not passed down
		const {
			// baseInput specific
			// inherited props
			value: oldValue,
			fireOnBlur,
			className,
			children,
			iconLeft,
			icon,
			iconRight,
			fullWidth,
			noBottomBorder,
			disabled,
			readOnly,
			title,
			textAlign,
			dir,
			textDir,
			hover,
			active,
			type,
			validationErrorText,
			successText,
			hasValidationError,
			isMoreInfo,
			moreInfoText,
			style,
			ariaLabelledBy,
			onChange,
			moreInfoClickHandler,
			onValueChange,
			onValueAccept,
			// all the other props
			...others
		} = this.props;
		const { displayValue: newValue } = this.state;

		const classNames = cx(_CLASS, {
			iconLeft: iconLeft || icon,
			iconRight,
			fullWidth,
			disabled: disabled || readOnly,
			active,
			hover,
			focus: this.state.showFocusClass,
			error: hasValidationError,
			success: successText && !hasValidationError,
			noBottomBorder,
		});

		const showMoreInfo = cx({
			moreInfo: isMoreInfo,
		});

		const iconLeftElement = createIcon(iconLeft || icon, 'left');
		const iconRightElement = createIcon(iconRight, 'right');
		const value =
			type === 'password'
				? undefined
				: (this._useOnValueCallbacks || fireOnBlur ? newValue : oldValue) || '';
		const ariaLabelledBySet = others['aria-label']
			? {}
			: {
					'aria-labelledby': `${ariaLabelledBy} ${this._id}__required-field ${this._id}__more-info ${this._id}__error-message ${this._id}__success-message`,
			  };

		return (
			<span
				{...(dir ? { dir } : {})}
				title={title}
				className={`${classNames} ${className}`}
				style={style}
			>
				{isMoreInfo && (
					<Link
						className={`more-info ${showMoreInfo}`}
						onClick={this._onMoreInfoClick}
						id={`${this._id}__more-info`}
						data-tid={`${this._id}__more-info`}
					>
						{moreInfoText}
					</Link>
				)}
				<input
					data-tid={`${this._ID}__input`}
					{...ariaLabelledBySet}
					{...sanitize(others, null)}
					value={value}
					onChange={this._onChange}
					onKeyDown={this._onKeyDown}
					onBlur={this._onBlur}
					onFocus={this._onFocus}
					disabled={disabled}
					readOnly={readOnly}
					aria-readonly={readOnly}
					data-fireonblur={fireOnBlur}
					onKeyUp={this._onKeyUp}
					onClick={this._onClick}
					autoComplete="off"
					type={type}
					ref={node => {
						this.inputNode = node;
					}}
					dir={resolveBaseTextDir(value, textDir, false)}
					style={textAlign ? { textAlign } : {}}
				/>
				{iconLeftElement}
				{iconRightElement}
				{!noBottomBorder && <hr className={`${_CLASS}__line`} />}
				{hasValidationError ? (
					<span className="validation-error" id={`${this._id}__error-message`}>
						{validationErrorText}
					</span>
				) : (
					<span className="success-message" id={`${this._id}__success-message`}>
						{successText}
					</span>
				)}
				{children}
			</span>
		);
	}
}
